import { useEffect, useId, useMemo, useRef, useState, type PointerEvent } from 'react';
import { CFG_MAX_STEPS } from '../../protocol/constants.ts';
import type { JigStep } from '../../protocol/types.ts';
import { compilePath, MAX_WAYPOINTS, PATH_ACTIONS, PATH_HEIGHT, PATH_WIDTH, pointOnGrid, waypoint, type Waypoint } from './path.ts';
import './PathEditor.css';

type Gesture = {
  pointerId: number;
  target: SVGSVGElement;
  index: number | null;
  clientX: number;
  clientY: number;
  offsetX: number;
  offsetY: number;
  moved: boolean;
};

function releaseCapture(gesture: Gesture) {
  if (gesture.target.hasPointerCapture(gesture.pointerId)) {
    gesture.target.releasePointerCapture(gesture.pointerId);
  }
}

function canPlot(point: Waypoint) {
  return Number.isFinite(point.x) && Number.isFinite(point.y) &&
    Math.abs(point.x) <= PATH_WIDTH / 2 && Math.abs(point.y) <= PATH_HEIGHT / 2;
}

export function PathEditor({ onUse, compact = false }: { onUse: (steps: JigStep[]) => void; compact?: boolean }) {
  const [points, setPoints] = useState<Waypoint[]>(() => [waypoint()]);
  const [selected, setSelected] = useState(0);
  const [speed, setSpeed] = useState(100);
  const [interacting, setInteracting] = useState(false);
  const gestureRef = useRef<Gesture | null>(null);
  const helpId = useId();
  const compiled = useMemo(() => compilePath(points, speed), [points, speed]);
  const point = points[selected]!;
  const last = points[points.length - 1]!;
  const drawable = points.every(canPlot);
  const usable = !interacting && compiled.problems.length === 0 && compiled.steps.length > 0;
  const emptyOrigin = points.length === 1 && point.action === 'none' && point.pauseMs === 0 &&
    Number.isInteger(speed) && speed >= 1 && speed <= 2000 && compiled.problems.length === 1;
  const hasActions = points.some((point) => point.action !== 'none');

  useEffect(() => () => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (gesture) releaseCapture(gesture);
  }, []);

  function updatePoint(index: number, change: Partial<Waypoint>) {
    setPoints((current) => current.map((p, i) => i !== index ? p :
      // Keep the origin invariant even if a synthetic change reaches a disabled input.
      i === 0 ? { ...p, ...change, x: 0, y: 0 } : { ...p, ...change }));
  }

  function addPoint(x = 0, y = 0) {
    if (points.length >= MAX_WAYPOINTS) return;
    setPoints((current) => [...current, waypoint(x, y)]);
    setSelected(points.length);
  }

  function finishGesture() {
    const gesture = gestureRef.current;
    // Clear BEFORE release: lostpointercapture may fire synchronously.
    gestureRef.current = null;
    setInteracting(false);
    if (gesture) releaseCapture(gesture);
  }

  function beginGesture(event: PointerEvent<SVGSVGElement>) {
    if (event.button !== 0 || event.isPrimary === false || gestureRef.current) return;
    const handle = event.target instanceof Element ? event.target.closest('[data-path-point]') : null;
    const index = handle ? Number(handle.getAttribute('data-path-point')) : null;
    if (index !== null) setSelected(index);
    if (index === null && points.length >= MAX_WAYPOINTS) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!pointOnGrid(event.clientX, event.clientY, rect)) return;
    const scale = Math.min(rect.width / PATH_WIDTH, rect.height / PATH_HEIGHT);
    const grabbed = index === null ? null : points[index]!;
    event.preventDefault();
    // This is ordinary pointer capture, never Pointer Lock. If capture fails,
    // leave the numeric controls usable rather than entering a stuck gesture.
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { return; }
    gestureRef.current = {
      pointerId: event.pointerId, target: event.currentTarget, index,
      clientX: event.clientX, clientY: event.clientY, moved: false,
      // Keep the grab offset in grid units, even for a handle extending beyond
      // the grid border. Reapply at the CURRENT scale so resizing stays correct.
      offsetX: grabbed ? (event.clientX - rect.left - rect.width / 2) / scale - grabbed.x : 0,
      offsetY: grabbed ? (event.clientY - rect.top - rect.height / 2) / scale - grabbed.y : 0,
    };
    setInteracting(true);
  }

  function updateGesture(gesture: Gesture, event: PointerEvent<SVGSVGElement>) {
    if (Math.hypot(event.clientX - gesture.clientX, event.clientY - gesture.clientY) > 4) gesture.moved = true;
    if (!gesture.moved || gesture.index === null || gesture.index === 0) return;
    const rect = gesture.target.getBoundingClientRect();
    const scale = Math.min(rect.width / PATH_WIDTH, rect.height / PATH_HEIGHT);
    const position = pointOnGrid(
      event.clientX - gesture.offsetX * scale, event.clientY - gesture.offsetY * scale, rect,
    );
    if (position) updatePoint(gesture.index, position);
  }

  function moveGesture(event: PointerEvent<SVGSVGElement>) {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    updateGesture(gesture, event);
  }

  function endGesture(event: PointerEvent<SVGSVGElement>) {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    updateGesture(gesture, event);
    const isBlankClick = gesture.index === null && !gesture.moved;
    const position = isBlankClick ? pointOnGrid(event.clientX, event.clientY, gesture.target.getBoundingClientRect()) : null;
    finishGesture();
    if (position) addPoint(position.x, position.y);
  }

  function cancelGesture(event: PointerEvent<SVGSVGElement>) {
    if (gestureRef.current?.pointerId === event.pointerId) finishGesture();
  }

  function clear() {
    finishGesture();
    setPoints([waypoint()]);
    setSelected(0);
  }

  return (
    <section className="panel path-editor">
      <h2>{compact ? 'Path' : 'Plot a path'}</h2>
      <p className="dim" id={helpId}>
        {compact ? 'Click to add · drag to move' : <>
          Click empty grid space to add points; drag numbered handles to move them. Or use Add point,
          select a point below and edit its coordinates. Point 1 is fixed at (0, 0).
        </>}
      </p>
      <p className="path-editor__return-note">
        {compact ? 'Returns to start · buttons released' : <>
          <strong>Dashed leg: automatic return to (0, 0) with all buttons released on every loop.</strong>
          {' '}The solid line follows your points in order.
        </>}
      </p>
      <div className="path-editor__plot">
        <svg className={`path-editor__grid${interacting ? ' path-editor__grid--active' : ''}`}
          viewBox={`${-PATH_WIDTH / 2} ${-PATH_HEIGHT / 2} ${PATH_WIDTH} ${PATH_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet" role="img" aria-label="Path plotting grid" aria-describedby={helpId}
          onPointerDown={beginGesture} onPointerMove={moveGesture} onPointerUp={endGesture}
          onPointerCancel={cancelGesture} onLostPointerCapture={cancelGesture}>
          <g className="path-editor__guides" aria-hidden="true">
            {Array.from({ length: PATH_WIDTH / 40 + 1 }, (_, i) => i * 40 - PATH_WIDTH / 2).map((x) =>
              <line key={`x${x}`} x1={x} y1={-PATH_HEIGHT / 2} x2={x} y2={PATH_HEIGHT / 2} />)}
            {Array.from({ length: PATH_HEIGHT / 40 + 1 }, (_, i) => i * 40 - PATH_HEIGHT / 2).map((y) =>
              <line key={`y${y}`} x1={-PATH_WIDTH / 2} y1={y} x2={PATH_WIDTH / 2} y2={y} />)}
            <line className="path-editor__axis" x1={-PATH_WIDTH / 2} y1={0} x2={PATH_WIDTH / 2} y2={0} />
            <line className="path-editor__axis" x1={0} y1={-PATH_HEIGHT / 2} x2={0} y2={PATH_HEIGHT / 2} />
            <text x={-310} y={-8}>−320</text><text x={272} y={-8}>+320 X</text>
            <text x={8} y={-182}>−200</text><text x={8} y={190}>+200 Y</text>
          </g>
          {drawable && <g className="path-editor__routes" aria-hidden="true">
            <polyline className="path-editor__route" points={points.map((p) => `${p.x},${p.y}`).join(' ')} />
            <line className="path-editor__return" x1={last.x} y1={last.y} x2={0} y2={0} />
          </g>}
          {points.map((p, index) => canPlot(p) && (
            <g key={index} data-path-point={index} transform={`translate(${p.x} ${p.y})`}
              className={`path-editor__handle${index === selected ? ' path-editor__handle--selected' : ''}${index === 0 ? ' path-editor__handle--fixed' : ''}`}>
              <title>{`Point ${index + 1}${index === 0 ? ' — fixed origin' : ''}: (${p.x}, ${p.y})`}</title>
              <circle r={12} /><text textAnchor="middle" dy="0.35em">{index + 1}</text>
            </g>
          ))}
        </svg>
      </div>
      <p className="dim path-editor__help">
        {compact ? 'Relative movement' : <>
          Approximate relative coordinates, not screen targets: X increases right, Y down. Pointer acceleration
          and screen edges affect actual playback. Plotting does not request pointer lock or send commands to the device.
        </>}
      </p>
      <div className="row row--between path-editor__toolbar">
        <span>{points.length} / {MAX_WAYPOINTS} points</span>
        <div className="row">
          <button type="button" className="btn btn--small" disabled={interacting || points.length >= MAX_WAYPOINTS}
            onClick={() => addPoint()}>Add point</button>
          <button type="button" className="btn btn--small" onClick={clear}>Clear path</button>
        </div>
      </div>
      {points.length >= MAX_WAYPOINTS && <p className="warn">Point limit reached. Remove a point before adding another.</p>}
      <div className="path-editor__details">
        <ol className="path-editor__points" aria-label="Path points">
          {points.map((p, index) => (
            <li key={index}>
              <button type="button" className={`btn btn--small path-editor__point${index === selected ? ' btn--on' : ''}`}
                aria-label={`Select point ${index + 1}${index === 0 ? ' (origin)' : ''}`} aria-pressed={index === selected}
                disabled={interacting} onClick={() => setSelected(index)}>
                <strong>{index + 1}{index === 0 ? ' · origin' : ''}</strong>
                <span>({Number.isFinite(p.x) ? p.x : '?'}, {Number.isFinite(p.y) ? p.y : '?'})</span>
                <span>{PATH_ACTIONS[p.action]}{p.pauseMs > 0 ? ` · ${p.pauseMs} ms pause` : ''}</span>
              </button>
            </li>
          ))}
        </ol>
        <fieldset className="path-editor__fields" disabled={interacting}>
          <legend>Point {selected + 1}{selected === 0 ? ' · fixed origin' : ''}</legend>
          {([
            ['x', 'Point X', -PATH_WIDTH / 2, PATH_WIDTH / 2],
            ['y', 'Point Y', -PATH_HEIGHT / 2, PATH_HEIGHT / 2],
          ] as const).map(([key, label, min, max]) => (
            <label className="path-editor__field" key={key}>
              <span>{label}</span>
              <input className="num" type="number" min={min} max={max} step={1} disabled={selected === 0}
                value={Number.isFinite(point[key]) ? point[key] : ''}
                onChange={(event) => updatePoint(selected, { [key]: event.target.valueAsNumber })} />
            </label>
          ))}
          <label className="path-editor__field">
            <span>Point action</span>
            <select className="text" value={point.action}
              onChange={(event) => updatePoint(selected, { action: event.target.value as Waypoint['action'] })}>
              {Object.entries(PATH_ACTIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="path-editor__field">
            <span>Pause at point (ms)</span>
            <input className="num" type="number" min={0} max={60000} step={1}
              value={Number.isFinite(point.pauseMs) ? point.pauseMs : ''}
              onChange={(event) => updatePoint(selected, { pauseMs: event.target.valueAsNumber })} />
          </label>
          <button type="button" className="btn btn--small" disabled={selected === 0} onClick={() => {
            if (selected === 0) return;
            setPoints((current) => current.filter((_, i) => i !== selected));
            setSelected(selected - 1);
          }}>Remove selected</button>
        </fieldset>
      </div>
      <label className="path-editor__field path-editor__speed">
        <span>Movement speed (px/s)</span>
        <input className="num" type="number" min={1} max={2000} step={1} disabled={interacting}
          value={Number.isFinite(speed) ? speed : ''} onChange={(event) => setSpeed(event.target.valueAsNumber)} />
      </label>
      {!compact && <p className="dim path-editor__help">
        Actions happen after arrival; pauses follow the action. Clicks hold for 50 ms then release for 50 ms.
        Pair Start left drag with End left drag to hold between points. Every report has a 2 ms minimum delay,
        so timing is approximate.
      </p>}
      {(!compact || hasActions) && <p className="warn path-editor__help">
        {compact ? 'Clicks and drags act at the pointer.' : <>
          Clicks and drags can activate whatever is under the cursor when the pattern runs.
          Creating a draft does not move or start the device.
        </>}
      </p>}
      {compiled.requiredSteps > 0 && <p className="dim">
        {compact ? `${(compiled.playbackMs / 1000).toFixed(2)} s per loop` : <>
          {compiled.requiredSteps} / {CFG_MAX_STEPS} steps · {(compiled.playbackMs / 1000).toFixed(2)} s per loop
          {' · '}{Math.round(compiled.pathLengthPx)} px including return
        </>}
      </p>}
      {compiled.problems.length > 0 && !(compact && emptyOrigin) && <ul className="problems path-editor__problems" aria-live="polite">
        {compiled.problems.map((problem) => <li key={problem}>{problem}</li>)}
      </ul>}
      <button type="button" className="btn btn--primary" disabled={!usable}
        onClick={() => { if (usable) onUse(compiled.steps); }}>{compact ? 'Use path' : 'Use path as pattern'}</button>
    </section>
  );
}
